import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  CalendarClock,
  MapPin,
  Calendar,
  PawPrint,
  HousePlus,
} from "lucide-react";
import axiosInstance from "../../../lib/axios";

const SearchContainer = ({ searchParams, onSearchParamsChange, authUser }) => {
  const navigate = useNavigate();

  // Get the current date without any time component for consistency
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Create min check-in date (tomorrow)
  const minCheckInDateObj = new Date(today);
  minCheckInDateObj.setDate(today.getDate() + 1);
  const minCheckInDate = minCheckInDateObj.toISOString().split("T")[0];

  // Calculate max date (exactly 3 months from today) - using UTC methods for consistency
  const maxDateObj = new Date(today);
  maxDateObj.setMonth(today.getMonth() + 3);
  const maxCheckInAndCheckOutDate = maxDateObj.toISOString().split("T")[0];

  // Calculate min CheckOut date (day after check-in or day after tomorrow if no check-in date)
  let minCheckOutDateObj;
  if (searchParams.checkInDate) {
    // Force UTC date parsing for consistency
    const [year, month, day] = searchParams.checkInDate.split("-").map(Number);
    minCheckOutDateObj = new Date(Date.UTC(year, month - 1, day + 1));
  } else {
    minCheckOutDateObj = new Date(today);
    minCheckOutDateObj.setDate(today.getDate() + 2);
  }
  const minCheckOutDate = minCheckOutDateObj.toISOString().split("T")[0];
  const locationOptions = ["Selangor", "Kuala Lumpur", "Negeri Sembilan"];

  const serviceTypeOptions = [{ display: "Pet Hotel", value: "Boarding" }];

  // Default weights and max weights by species
  const defaultWeights = {
    Cat: 4,
    Dog: 15,
  };

  const maxWeights = {
    Cat: 15,
    Dog: 60,
  };

  // States
  const [isSearching, setIsSearching] = useState(false);
  const [userPets, setUserPets] = useState([]);
  const [isLoadingPets, setIsLoadingPets] = useState(true);
  const [tempWeights, setTempWeights] = useState({});
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState({});
  const UPDATE_THRESHOLD_MS = 500;

  // For Spam protection
  const updatePetWithSpamProtection = (index, field, value) => {
    if (field === "weight") {
      const now = Date.now();
      const lastUpdate = lastUpdateTimestamp[index] || 0;

      // Only allow update if enough time has passed
      if (now - lastUpdate >= UPDATE_THRESHOLD_MS) {
        setLastUpdateTimestamp((prev) => ({
          ...prev,
          [index]: now,
        }));

        // Proceed with the update
        const newPets = [...searchParams.pets];
        newPets[index] = { ...newPets[index], weight: value };
        onSearchParamsChange({
          ...searchParams,
          pets: newPets,
        });
      }
      // Ignore updates that happen too quickly
    } else {
      // For non-weight fields, update normally
      updatePet(index, field, value);
    }
  };

  // Fetch user's pets when component mounts
  useEffect(() => {
    const fetchUserPets = async () => {
      if (!authUser) {
        // Scenario 1: User not logged in
        setUserPets([]);
        onSearchParamsChange({
          ...searchParams,
          useExistingPets: false,
          pets: [
            {
              species: "Cat",
              weight: 4,
            },
          ],
        });
        setIsLoadingPets(false);
        return;
      }

      try {
        setIsLoadingPets(true);
        const response = await axiosInstance.get("/pets/my-pets");
        setUserPets(response.data);

        // Scenario 2 & 3: User is logged in
        if (response.data.length > 0) {
          // Scenario 3: User has registered pets
          onSearchParamsChange({
            ...searchParams,
            useExistingPets: true,
            pets: response.data,
          });
        } else {
          // Scenario 2: User is logged in but has no pets
          onSearchParamsChange({
            ...searchParams,
            useExistingPets: false,
            pets: [
              {
                species: "Cat",
                weight: 4,
              },
            ],
          });
        }
      } catch (error) {
        // Only show toast error for non-auth errors
        if (!error.suppressLog) {
          toast.error("Failed to fetch pets");
          console.error("Error fetching pets:", error);
        }

        // Set default pets regardless of error type
        setUserPets([]);
        onSearchParamsChange({
          ...searchParams,
          useExistingPets: false,
          pets: [
            {
              species: "Cat",
              weight: 4,
            },
          ],
        });
      } finally {
        setIsLoadingPets(false);
      }
    };

    fetchUserPets();
  }, [authUser]);

  const handlePetSelection = (petId) => {
    const selectedPet = userPets.find((pet) => pet._id === petId);
    if (selectedPet) {
      const isAlreadySelected = searchParams.pets.some((p) => p._id === petId);
      if (isAlreadySelected) {
        // Only allow deselection if more than one pet is selected
        if (searchParams.pets.length > 1) {
          onSearchParamsChange({
            ...searchParams,
            pets: searchParams.pets.filter((p) => p._id !== petId),
          });
        } else {
          toast.error("At least one pet must be selected for searching");
        }
      } else {
        // Add the pet if it's not selected
        onSearchParamsChange({
          ...searchParams,
          pets: [...searchParams.pets, selectedPet],
        });
      }
    }
  };

  /**
   * Function to add pet into search container
   */
  const addPet = () => {
    if (searchParams.pets.length < 4) {
      onSearchParamsChange({
        ...searchParams,
        pets: [
          ...searchParams.pets,
          {
            species: "Cat",
            weight: 4,
          },
        ],
      });
    } else {
      toast.error("Maximum 4 pets allowed");
    }
  };

  /**
   * Function to remove pet into search container
   */
  const removePet = (index) => {
    if (searchParams.pets.length > 1) {
      onSearchParamsChange({
        ...searchParams,
        pets: searchParams.pets.filter((_, i) => i !== index),
      });
    } else {
      toast.error("Minimum 1 pet required");
    }
  };

  /**
   * Function to update pet into search container
   */
  const updatePet = (index, field, value) => {
    const newPets = [...searchParams.pets];

    // If species is changed, update the default weight
    if (field === "species") {
      newPets[index] = {
        ...newPets[index],
        [field]: value,
        weight: defaultWeights[value] || newPets[index].weight,
      };

      // Also update the tempWeights if we're changing species
      setTempWeights({
        ...tempWeights,
        [index]: defaultWeights[value],
      });

      onSearchParamsChange({
        ...searchParams,
        pets: newPets,
      });
    } else if (field === "weight") {
      // Simply update the pet weight directly
      newPets[index] = { ...newPets[index], [field]: value };
      onSearchParamsChange({
        ...searchParams,
        pets: newPets,
      });
    } else {
      newPets[index] = { ...newPets[index], [field]: value };
      onSearchParamsChange({
        ...searchParams,
        pets: newPets,
      });
    }
  };

  /**
   * Handle check-in date change and automatically update check-out date if needed
   */
  const handleCheckInDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const maxAllowedDate = new Date(maxCheckInAndCheckOutDate);
    const today = new Date();

    // Validate that the selected date is not after the max allowed date
    if (selectedDate > maxAllowedDate) {
      // If date is out of allowed range, reset to max allowed date
      onSearchParamsChange({
        ...searchParams,
        checkInDate: maxCheckInAndCheckOutDate,
        checkOutDate: searchParams.CheckOutDate,
      });
      alert("You cannot select a date more than 3 months in the future.");
      return;
    }

    if (selectedDate < today) {
      // If date is in the past, reset to tomorrow
      onSearchParamsChange({
        ...searchParams,
        checkInDate: minCheckInDate,
        checkOutDate: searchParams.checkOutDate,
      });
      alert("You cannot select a date in the past.");
      return;
    }

    // Continue with your existing logic
    const newCheckInDate = e.target.value;
    const currentCheckOutDate = searchParams.checkOutDate;
    let newCheckOutDate = currentCheckOutDate;

    if (
      !currentCheckOutDate ||
      new Date(newCheckInDate) >= new Date(currentCheckOutDate)
    ) {
      const nextDay = new Date(e.target.value);
      nextDay.setDate(nextDay.getDate() + 1);
      newCheckOutDate = nextDay.toISOString().split("T")[0];
    }

    onSearchParamsChange({
      ...searchParams,
      checkInDate: newCheckInDate,
      checkOutDate: newCheckOutDate,
    });
  };
  /**
   * Function to pass in search values into the ResultPage and search for resulting hotels
   * @returns
   */
  const handleSearch = async () => {
    try {
      if (!searchParams.checkInDate || !searchParams.checkOutDate) {
        toast.error("Please select both Check-in and Check-out dates");
        return;
      }

      const start = new Date(searchParams.checkInDate);
      const end = new Date(searchParams.checkOutDate);
      if (start >= end) {
        toast.error("Check-out date must be after Check-in date");
        return;
      }

      const today = new Date(); // midnight today
      today.setHours(0, 0, 0, 0);
      const maxAllowedDate = new Date(today);
      maxAllowedDate.setMonth(today.getMonth() + 3); // +3 months

      if (start < today || end < today) {
        toast.error("Dates must be in the future");
        // set checkInDate and checkOutDate to today
        onSearchParamsChange({
          ...searchParams,
          checkInDate: minCheckInDate,
          checkOutDate: minCheckOutDate,
        });
        return;
      }

      if (start > maxAllowedDate || end > maxAllowedDate) {
        toast.error("Dates must be within 3 months from today");
        // set checkInDate and checkOutDate to maxAllowedDate
        onSearchParamsChange({
          ...searchParams,
          checkInDate: minCheckInDate,
          checkOutDate: minCheckOutDate,
        });
        return;
      }

      setIsSearching(true);

      const queryParams = new URLSearchParams();

      // Add non-array parameters
      queryParams.set("serviceType", searchParams.serviceType);
      queryParams.set("location", searchParams.location);
      queryParams.set("startDate", searchParams.checkInDate);
      queryParams.set("endDate", searchParams.checkOutDate);

      //Group pets by type before sending
      const petsByType = {};
      searchParams.pets.forEach((pet, index) => {
        const species = pet.species;
        if (!petsByType[species]) {
          petsByType[species] = [];
        }
        petsByType[species].push({
          type: species,
          weight: pet.weight,
          _id: pet._id || null,
          name: pet.name || `Pet ${index + 1}`,
          petImg: pet.petImg || null,
          breed: pet.breed || null,
        });
      });

      // Also calculate min/max weights on the client side
      const minMaxWeights = {};
      Object.keys(petsByType).forEach((species) => {
        const weights = petsByType[species].map((pet) => Number(pet.weight));
        minMaxWeights[species] = {
          min: Math.min(...weights),
          max: Math.max(...weights),
        };
      });

      // Set the optimized data structure in query params
      queryParams.set(
        "petData",
        JSON.stringify({
          petsByType,
          minMaxWeights,
        })
      );

      let response;
      if (searchParams.allowSharedRooms) {
        response = await fetch(`/api/hotels/searchSharedRooms?${queryParams}`);
      } else {
        response = await fetch(`/api/hotels/search?${queryParams}`);
      }
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Search failed");
      }

      if (data.length === 0) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        navigate("/no-results", { state: { ...searchParams } });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
        navigate("/results", {
          state: {
            results: data,
            searchParams: { ...searchParams },
          },
        });
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed: " + error.message);
    } finally {
      setIsSearching(false);
    }
  };

  // If pets are still loading, show a loading state for the search container
  if (isLoadingPets) {
    return (
      <div className="px-4 mb-12">
        <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-white/20 p-4 md:p-6 min-h-[300px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B46B42] mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">
              Loading pet information...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 mb-12">
      <div className="max-w-4xl mx-auto bg-[#F4EBE2] rounded-xl shadow-lg p-4 md:p-6">
        <div className="space-y-4 mb-6">
          {/* Service Type & Location Row - Combined on mobile, separate on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Service Type Select */}
            <div className="w-full group">
              <label className="flex text-sm md:text-base font-semibold text-gray-700 mb-2 items-center gap-1 md:gap-2">
                <HousePlus className="w-4 h-4 md:w-5 md:h-5 text-[#e3712e] group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">Pet Service</span>
                <span className="sm:hidden">Service</span>
              </label>
              <div className="relative">
                <select
                  className="w-full h-12 md:h-14 pl-3 pr-8 text-sm md:text-base appearance-none border border-gray-300 rounded-lg bg-white/90 focus:outline-none transition-all duration-200"
                  value={searchParams.serviceType}
                  onChange={(e) =>
                    onSearchParamsChange({
                      ...searchParams,
                      serviceType: e.target.value,
                    })
                  }
                >
                  {serviceTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.display}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Location Select */}
            <div className="w-full group">
              <label className="flex text-sm md:text-base font-semibold text-gray-700 mb-2 items-center gap-1 md:gap-2">
                <MapPin className="w-4 h-4 md:w-5 md:h-5 text-[#e3712e] group-hover:scale-110 transition-transform" />
                Location
              </label>
              <div className="relative">
                <select
                  className="w-full h-12 md:h-14 pl-3 pr-8 text-sm md:text-base appearance-none border border-gray-300 rounded-lg bg-white/90 focus:outline-none transition-all duration-200"
                  value={searchParams.location}
                  onChange={(e) =>
                    onSearchParamsChange({
                      ...searchParams,
                      location: e.target.value,
                    })
                  }
                >
                  {locationOptions.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Check-In Date - Hidden on mobile, shown on desktop */}
            <div className="hidden md:block w-full group">
              <label className="flex text-sm md:text-base font-semibold text-gray-700 mb-2 items-center gap-2">
                <Calendar className="w-5 h-5 text-[#e3712e] group-hover:scale-110 transition-transform" />
                Check-in
              </label>
              <input
                type="date"
                min={minCheckInDate}
                max={maxCheckInAndCheckOutDate}
                value={searchParams.checkInDate}
                onChange={handleCheckInDateChange}
                onKeyDown={(e) => e.preventDefault()}
                className="w-full h-14 px-3 text-base appearance-none border border-gray-300 rounded-lg bg-white/90 focus:outline-none transition-all duration-200"
              />
            </div>

            {/* Check-Out Date - Hidden on mobile, shown on desktop */}
            <div className="hidden md:block w-full group">
              <label className="flex text-sm md:text-base font-semibold text-gray-700 mb-2 items-center gap-2">
                <CalendarClock className="w-5 h-5 text-[#e3712e] group-hover:scale-110 transition-transform" />
                Check-out
              </label>
              <input
                type="date"
                min={minCheckOutDate}
                max={maxCheckInAndCheckOutDate}
                value={searchParams.checkOutDate}
                onChange={(e) =>
                  onSearchParamsChange({
                    ...searchParams,
                    checkOutDate: e.target.value,
                  })
                }
                onKeyDown={(e) => e.preventDefault()}
                className="w-full h-14 px-3 text-base appearance-none border border-gray-300 rounded-lg bg-white/90 focus:outline-none transition-all duration-200"
              />
            </div>
          </div>

          {/* Dates Row - Only visible on mobile */}
          <div className="md:hidden grid grid-cols-2 gap-4">
            {/* Check-In Date */}
            <div className="w-full group">
              <label className="flex text-sm font-semibold text-gray-700 mb-2 items-center gap-1">
                <Calendar className="w-4 h-4 text-[#e3712e] group-hover:scale-110 transition-transform" />
                Check-in
              </label>
              <input
                type="date"
                min={minCheckInDate}
                max={maxCheckInAndCheckOutDate}
                value={searchParams.checkInDate}
                onChange={handleCheckInDateChange}
                onKeyDown={(e) => e.preventDefault()}
                className="w-full h-12 px-3 text-sm appearance-none border border-gray-300 rounded-lg bg-white/90 focus:outline-none transition-all duration-200"
              />
            </div>

            {/* Check-Out Date */}
            <div className="w-full group">
              <label className="flex text-sm font-semibold text-gray-700 mb-2 items-center gap-1">
                <CalendarClock className="w-4 h-4 text-[#e3712e] group-hover:scale-110 transition-transform" />
                Check-out
              </label>
              <input
                type="date"
                min={minCheckOutDate}
                max={maxCheckInAndCheckOutDate}
                value={searchParams.checkOutDate}
                onChange={(e) =>
                  onSearchParamsChange({
                    ...searchParams,
                    checkOutDate: e.target.value,
                  })
                }
                onKeyDown={(e) => e.preventDefault()}
                className="w-full h-12 px-3 text-sm appearance-none border border-gray-300 rounded-lg bg-white/90 focus:outline-none transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {/* Pets Section */}
        <div className="space-y-3 mt-4">
          <div className="flex justify-between items-center gap-3 mb-3">
            <div className="flex items-center gap-2">
              <PawPrint className="w-4 h-4 md:w-5 md:h-5 text-[#e3712e]" />
              <span className="text-sm md:text-base text-gray-800">
                {authUser && userPets.length > 0 ? "Your Pets" : "Pet Details"}
              </span>
            </div>

            {/* Button (Register and add pets) */}
            <div className="flex items-center gap-2">
              {authUser ? (
                userPets.length > 0 ? (
                  // Logged in with registered pets: Only Register More Pets button
                  <button
                    onClick={() =>
                      navigate("/account-page", {
                        state: { authUser, initialTab: "pets" },
                      })
                    }
                    className="flex items-center justify-center gap-1 bg-gradient-to-r from-[#f58849] to-[#eba46e] text-white px-2 py-1 rounded-md hover:shadow-lg hover:translate-y-[-1px] transition-all duration-200 shadow-md text-xs"
                  >
                    <PawPrint className="w-3 h-3" />
                    <span className="hidden sm:inline whitespace-nowrap">
                      Register More Pets
                    </span>
                    <span className="sm:hidden">Register More Pets</span>
                  </button>
                ) : (
                  // Logged in but no pets: Register Your Pets and Add Pet buttons
                  <>
                    <button
                      onClick={() =>
                        navigate("/account-page", {
                          state: { authUser, initialTab: "pets" },
                        })
                      }
                      className="flex items-center justify-center gap-1 bg-gradient-to-r from-[#e98c55] to-[#dfa981] text-white px-2 py-1 rounded-md hover:shadow-lg hover:translate-y-[-1px] transition-all duration-200 shadow-md text-xs"
                    >
                      <PawPrint className="w-3 h-3" />
                      <span className="hidden sm:inline whitespace-nowrap">
                        Register Your Pets
                      </span>
                      <span className="sm:hidden">Register Your Pets</span>
                    </button>
                    <button
                      onClick={addPet}
                      className="flex items-center justify-center gap-1 bg-gradient-to-r from-[#e98c55] to-[#dfa981] text-white px-2 py-1 rounded-md hover:shadow-lg hover:translate-y-[-1px] transition-all duration-200 shadow-md text-xs"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      <span className="hidden sm:inline whitespace-nowrap">
                        Add Pet
                      </span>
                      <span className="sm:hidden">Add Pet</span>
                    </button>
                  </>
                )
              ) : (
                // Not logged in: Only Add Pet button
                <button
                  onClick={addPet}
                  className="flex items-center justify-center gap-1 bg-gradient-to-r from-[#e98c55] to-[#dfa981] text-white px-2 py-1 rounded-md hover:shadow-lg hover:translate-y-[-1px] transition-all duration-200 shadow-md text-xs"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <span className="hidden sm:inline whitespace-nowrap">
                    Add Pet
                  </span>
                  <span className="sm:hidden">Add Pet</span>
                </button>
              )}
            </div>
          </div>

          {/* Pet Cards Section */}
          {authUser && userPets.length > 0 ? (
            // Registered Pets - Grid on desktop, vertical stack on mobile
            <div className="space-y-2 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-3">
              {userPets.map((pet) => (
                <div
                  key={pet._id}
                  onClick={() => handlePetSelection(pet._id)}
                  className={`
                    bg-white rounded-lg p-3 cursor-pointer transform transition-all duration-200
                    hover:shadow-md hover:-translate-y-0.5
                    ${
                      searchParams.pets.some((p) => p._id === pet._id)
                        ? "ring-2 ring-[#e98c55] shadow-sm"
                        : "border border-gray-100"
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    {pet.petImg ? (
                      <img
                        src={pet.petImg}
                        alt={pet.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <PawPrint className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="text-sm font-semibold text-gray-800 truncate">
                          {pet.name}
                        </h3>
                        <span className="px-2 py-0.5 bg-[#e98c55]/10 text-[#e98c55] rounded-full text-xs font-medium ml-2 flex-shrink-0">
                          {pet.species}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-600">
                        <span>{pet.weight || "N/A"} kg</span>
                        {pet.breed && (
                          <span className="truncate max-w-[120px]">
                            {pet.breed}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Default Pet Cards - Compact vertical cards
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {searchParams.pets.map((pet, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  {/* Header */}
                  <div className="bg-[#e98c55]/10 px-3 py-2 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-gray-800">
                      Pet {index + 1}
                    </h3>
                    {searchParams.pets.length > 1 && (
                      <button
                        onClick={() => removePet(index)}
                        className="text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 transition-colors text-xs"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        Remove
                      </button>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-3 space-y-3">
                    {/* Species Selection */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Species
                      </label>
                      <select
                        className="block w-full p-2 text-sm border border-gray-300 rounded-lg bg-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#e98c55]/20"
                        value={pet.species}
                        onChange={(e) =>
                          updatePet(index, "species", e.target.value)
                        }
                      >
                        <option value="Cat">Cat</option>
                        <option value="Dog">Dog</option>
                      </select>
                    </div>

                    {/* Weight Selection */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="block text-xs font-medium text-gray-700">
                          Weight
                        </label>
                        <span className="text-sm font-semibold text-[#e98c55]">
                          {tempWeights[index] !== undefined
                            ? tempWeights[index]
                            : pet.weight}{" "}
                          kg
                        </span>
                      </div>

                      {/* Weight Slider */}
                      <div className="relative">
                        <input
                          type="range"
                          min="1"
                          max={maxWeights[pet.species]}
                          step="0.5"
                          value={
                            tempWeights[index] !== undefined
                              ? tempWeights[index]
                              : pet.weight
                          }
                          onChange={(e) => {
                            setTempWeights({
                              ...tempWeights,
                              [index]: parseFloat(e.target.value),
                            });
                          }}
                          onMouseUp={(e) => {
                            updatePetWithSpamProtection(
                              index,
                              "weight",
                              parseFloat(e.target.value)
                            );
                          }}
                          onClick={(e) => {
                            updatePetWithSpamProtection(
                              index,
                              "weight",
                              parseFloat(e.target.value)
                            );
                          }}
                          onTouchEnd={(e) => {
                            updatePetWithSpamProtection(
                              index,
                              "weight",
                              parseFloat(e.target.value)
                            );
                          }}
                          className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#e98c55]"
                        />

                        {/* Weight Scale */}
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>1kg</span>
                          {pet.species === "Cat" ? (
                            <>
                              <span>8kg</span>
                              <span>15kg</span>
                            </>
                          ) : (
                            <>
                              <span>30kg</span>
                              <span>60kg</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search Button */}
        <div className="text-center mt-6">
          <div className="relative inline-block">
            <button
              className="bg-gradient-to-r from-[#f58849] to-[#eba46e] text-white font-bold py-3 md:py-4 px-10 md:px-12 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:transform-none relative overflow-hidden group text-sm md:text-base"
              onClick={handleSearch}
              disabled={isSearching}
            >
              <span className="absolute top-0 left-0 w-full h-full overflow-hidden">
                <span className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:left-[100%] transition-all duration-700 ease-in-out"></span>
              </span>

              {isSearching ? (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4 md:h-5 md:w-5"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Searching...</span>
                </div>
              ) : (
                <span className="flex items-center justify-center gap-2 relative z-10">
                  <span>Book Now</span>
                  <span className="text-sm animate-pulse group-hover:scale-125 transition-transform">
                    🔍
                  </span>
                </span>
              )}
            </button>
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              Save RM20
            </span>
          </div>
        </div>

        {/* Trust Icons Row */}
        <div className="mt-2 pt-4">
          <div className="flex items-center justify-center space-x-4 md:space-x-6">
            <div className="flex items-center space-x-2 text-gray-600">
              <span className="text-lg md:text-xl">🏢</span>
              <span className="text-xs md:text-sm font-medium">
                SSM Registered
              </span>
            </div>

            <div className="flex items-center space-x-2 text-gray-600">
              <span className="text-lg md:text-xl">✅</span>
              <span className="text-xs md:text-sm font-medium">
                Verified by Stayfur
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchContainer;
